import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "col-md-3 mb-2 d-flex align-items-end" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = { class: "staff-name" }
const _hoisted_4 = { class: "date-display" }
const _hoisted_5 = { class: "badge bg-danger" }
const _hoisted_6 = { class: "badge bg-success" }
const _hoisted_7 = { class: "badge bg-secondary total-badge" }

import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { formatIsoDate } from "@/utils/format"
import { TicketType } from "shared/Ticket"
import { User } from "shared/User"
import { apiPost } from "@/services/api/client";
import { panelRequest, isForbidden, reportPanelError } from "@/components/panel/panelAccess";

// Import modular components
import BaseTableComponent from "@/components/shared/BaseTableComponent.vue"
import ActionButtonsComponent from "@/components/shared/ActionButtonsComponent.vue"
import FilterRowComponent from "@/components/shared/filters/FilterRowComponent.vue"
import SelectFilterComponent from "@/components/shared/filters/SelectFilterComponent.vue"
import DateFilterComponent from "@/components/shared/filters/DateFilterComponent.vue"

interface Props {
  user: User
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StaffTableComponent',
  props: {
    user: {}
  },
  setup(__props: any) {

const instance = getCurrentInstance()
const t = (key: string, named?: Record<string, unknown>): string => (instance!.proxy as any).$t(key, named ?? {})

// Props
const props = __props

// Reactive data
const staffData = ref<any[]>([])
const loadingStaff = ref(false)
const forbidden = ref(false)
const totalStaffData = ref(0)
const currentPage = ref(1)
const itemsPerPage = ref(25)

// Filters
const selectedStaff = ref('')
const selectedType = ref('all')
const selectedStatus = ref<'all' | number>('all')
const dateFrom = ref('')
const dateTo = ref('')
const sortBy = ref('date')

// Filter options
const staffNames = ref<string[]>([])
const staffOptions = computed(() => [{ value: '', label: t('panel.staff.options.allStaff') }].concat(
  staffNames.value.map(name => ({ value: name, label: name }))
))

const statusOptions = computed(() => [
  { value: 'all', label: t('panel.staff.options.all') },
  { value: 0, label: t('panel.staff.options.opened') },
  { value: 1, label: t('panel.staff.options.closed') }
])

const updateStaffOptions = () => {
  // Extract unique staff names from the current data
  staffNames.value = [...new Set(staffData.value.map(entry => entry.staffName))].sort() as string[]
}

const ticketTypeOptions = computed(() => [
  { value: 'all', label: t('panel.staff.options.allTypes') },
  { value: TicketType.REPORT, label: t('panel.staff.options.reportsOnly') },
  { value: TicketType.SUPPORT, label: t('panel.staff.options.supportOnly') }
])

const sortOptions = computed(() => [
  { value: 'date', label: t('panel.staff.sort.date') },
  { value: 'staff', label: t('panel.staff.sort.staff') },
  { value: 'reports', label: t('panel.staff.sort.reports') },
  { value: 'support', label: t('panel.staff.sort.support') },
  { value: 'total', label: t('panel.staff.sort.total') }
])

const itemsPerPageOptions = [
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' }
]

// Methods
const buildQuery = () => {
  const query: any = {
    page: currentPage.value,
    pageSize: itemsPerPage.value,
    sortBy: sortBy.value
  }

  if (selectedStaff.value) {
    query.staffName = selectedStaff.value
  }

  if (selectedType.value !== 'all') {
    query.ticketType = selectedType.value
  }

  if (selectedStatus.value !== 'all') {
    query.status = Number(selectedStatus.value)
  } else {
    query.status = 'all'
  }

  if (dateFrom.value) {
    query.dateFrom = new Date(dateFrom.value + 'T00:00:00Z').getTime()
  }

  if (dateTo.value) {
    query.dateTo = new Date(dateTo.value + 'T23:59:59Z').getTime()
  }

  return query
}

const fetchStaffData = async () => {
  loadingStaff.value = true
  try {
    const query = buildQuery()
    const response = await apiPost('/staff/performance', query, panelRequest) as unknown as { data: any[]; total: number }
    
    staffData.value = (response as any).data || []
    totalStaffData.value = Number((response as any).total) || 0
    
    // Update staff options based on the data we just received
    updateStaffOptions()
  } catch (error) {
    if (isForbidden(error)) forbidden.value = true
    else reportPanelError('Error fetching staff data:', error)
    staffData.value = []
    totalStaffData.value = 0
  } finally {
    loadingStaff.value = false
  }
}



const applyFilters = async () => {
  currentPage.value = 1
  await fetchStaffData()
}

const refreshStaff = async () => {
  await fetchStaffData()
}

const goToPage = (page: number) => {
  if (page >= 1) {
    currentPage.value = page
    fetchStaffData()
  }
}

const downloadCSV = async () => {
  try {
    const query = buildQuery()
    const blob = await apiPost('/staff/performance/csv', query, {
      responseType: 'blob',
      skipSwal: true
    }) as unknown as Blob
    const text = await blob.text()
    const downloadBlob = new Blob([text], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `staff_performance_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (error) {
    console.error('Error downloading CSV:', error)
    alert(t('panel.staff.errors.downloadCsv'))
  }
}

const downloadJSON = async () => {
  try {
    const query = buildQuery()
    const blob = await apiPost('/staff/performance/json', query, {
      responseType: 'blob',
      skipSwal: true
    }) as unknown as Blob
    const text = await blob.text()
    const downloadBlob = new Blob([text], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `staff_performance_${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (error) {
    console.error('Error downloading JSON:', error)
    alert(t('panel.staff.errors.downloadJson'))
  }
}



// Lifecycle
onMounted(async () => {
  await fetchStaffData()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseTableComponent, {
    title: t('panel.staff.title'),
    "section-id": "staff-performance",
    "item-name": t('panel.staff.itemName'),
    "paginated-data": staffData.value,
    "total-items": totalStaffData.value,
    "current-page": currentPage.value,
    "items-per-page": itemsPerPage.value,
    loading: loadingStaff.value,
    forbidden: forbidden.value,
    onRefresh: refreshStaff,
    onPageChange: goToPage
  }, {
    "header-actions": _withCtx(() => [
      _createVNode(ActionButtonsComponent, {
        loading: loadingStaff.value,
        onDownloadCsv: downloadCSV,
        onDownloadJson: downloadJSON,
        onRefresh: refreshStaff
      }, null, 8, ["loading"])
    ]),
    filters: _withCtx(() => [
      _createVNode(FilterRowComponent, null, {
        default: _withCtx(() => [
          _createVNode(SelectFilterComponent, {
            label: t('panel.staff.filters.staffMember'),
            modelValue: selectedStaff.value,
            "onUpdate:modelValue": [
              _cache[0] || (_cache[0] = ($event: any) => ((selectedStaff).value = $event)),
              applyFilters
            ],
            options: staffOptions.value
          }, null, 8, ["label", "modelValue", "options"]),
          _createVNode(SelectFilterComponent, {
            label: t('panel.staff.filters.status'),
            modelValue: selectedStatus.value,
            "onUpdate:modelValue": [
              _cache[1] || (_cache[1] = ($event: any) => ((selectedStatus).value = $event)),
              applyFilters
            ],
            options: statusOptions.value
          }, null, 8, ["label", "modelValue", "options"]),
          _createVNode(SelectFilterComponent, {
            label: t('panel.staff.filters.ticketType'),
            modelValue: selectedType.value,
            "onUpdate:modelValue": [
              _cache[2] || (_cache[2] = ($event: any) => ((selectedType).value = $event)),
              applyFilters
            ],
            options: ticketTypeOptions.value
          }, null, 8, ["label", "modelValue", "options"]),
          _createVNode(DateFilterComponent, {
            label: t('panel.staff.filters.dateFrom'),
            modelValue: dateFrom.value,
            "onUpdate:modelValue": [
              _cache[3] || (_cache[3] = ($event: any) => ((dateFrom).value = $event)),
              applyFilters
            ]
          }, null, 8, ["label", "modelValue"]),
          _createVNode(DateFilterComponent, {
            label: t('panel.staff.filters.dateTo'),
            modelValue: dateTo.value,
            "onUpdate:modelValue": [
              _cache[4] || (_cache[4] = ($event: any) => ((dateTo).value = $event)),
              applyFilters
            ]
          }, null, 8, ["label", "modelValue"])
        ]),
        _: 1
      }),
      _createVNode(FilterRowComponent, null, {
        default: _withCtx(() => [
          _createVNode(SelectFilterComponent, {
            label: t('panel.staff.filters.sortBy'),
            modelValue: sortBy.value,
            "onUpdate:modelValue": [
              _cache[5] || (_cache[5] = ($event: any) => ((sortBy).value = $event)),
              applyFilters
            ],
            options: sortOptions.value
          }, null, 8, ["label", "modelValue", "options"]),
          _createVNode(SelectFilterComponent, {
            label: t('panel.staff.filters.itemsPerPage'),
            modelValue: itemsPerPage.value,
            "onUpdate:modelValue": [
              _cache[6] || (_cache[6] = ($event: any) => ((itemsPerPage).value = $event)),
              applyFilters
            ],
            options: itemsPerPageOptions
          }, null, 8, ["label", "modelValue"]),
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("button", {
              class: "btn btn-primary w-100",
              onClick: applyFilters,
              disabled: loadingStaff.value
            }, [
              _cache[7] || (_cache[7] = _createElementVNode("i", { class: "fa fa-filter me-1" }, null, -1)),
              _createTextVNode(" " + _toDisplayString(t('panel.staff.filters.apply')), 1)
            ], 8, _hoisted_2)
          ])
        ]),
        _: 1
      })
    ]),
    "table-header": _withCtx(() => [
      _createElementVNode("th", null, _toDisplayString(t('panel.staff.columns.staff')), 1),
      _createElementVNode("th", null, _toDisplayString(t('panel.staff.columns.date')), 1),
      _createElementVNode("th", null, _toDisplayString(t('panel.staff.columns.reports')), 1),
      _createElementVNode("th", null, _toDisplayString(t('panel.staff.columns.support')), 1),
      _createElementVNode("th", null, _toDisplayString(t('panel.staff.columns.total')), 1)
    ]),
    "table-body": _withCtx(({ data }) => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data || [], (entry) => {
        return (_openBlock(), _createElementBlock("tr", {
          key: entry._id,
          class: "staff-entry-row"
        }, [
          _createElementVNode("td", null, [
            _createElementVNode("span", _hoisted_3, _toDisplayString(entry.staffName), 1)
          ]),
          _createElementVNode("td", null, [
            _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(formatIsoDate)(entry.date)), 1)
          ]),
          _createElementVNode("td", null, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(entry.reportsCount || 0), 1)
          ]),
          _createElementVNode("td", null, [
            _createElementVNode("span", _hoisted_6, _toDisplayString(entry.supportCount || 0), 1)
          ]),
          _createElementVNode("td", null, [
            _createElementVNode("span", _hoisted_7, _toDisplayString((entry.reportsCount || 0) + (entry.supportCount || 0)), 1)
          ])
        ]))
      }), 128))
    ]),
    _: 1
  }, 8, ["title", "item-name", "paginated-data", "total-items", "current-page", "items-per-page", "loading", "forbidden"]))
}
}

})